import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getMember, validateApiResponse } from '../../../services';
import { mapApiMemberToUi } from '../utils/mapApiMemberToUi';

const errorKind = (err) => {
  const status = err?.response?.status;
  if (status === 404) return 'notFound';
  if (status === 403) return 'forbidden';
  return 'failed';
};

/**
 * Loads one member for the detail page: `{ member, isLoading, error, refetch, setMember }`.
 * `error` is `'notFound' | 'forbidden' | 'failed' | null`. `setMember` lets an action replace
 * the member with the server's answer without a second round trip.
 *
 * Mount it with `key={memberId}` so a different member starts from the loading state.
 *
 * @param {string} memberId
 */
export const useMember = (memberId) => {
  const [state, setState] = useState({ member: null, isLoading: true, error: null });
  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);

  const load = useCallback(() => {
    fetchIdRef.current += 1;
    const fetchId = fetchIdRef.current;
    return getMember(memberId)
      .then((res) => validateApiResponse(res, 'Could not load the member'))
      .then(({ body }) => ({ member: mapApiMemberToUi(body), isLoading: false, error: null }))
      .catch((err) => ({ member: null, isLoading: false, error: errorKind(err) }))
      .then((next) => {
        if (mountedRef.current && fetchId === fetchIdRef.current) setState(next);
      });
  }, [memberId]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  // Retry: back to the loading state so there is feedback and the button cannot be spammed (CT-5832).
  const refetch = useCallback(() => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    return load();
  }, [load]);

  const setMember = useCallback((member) => setState((s) => ({ ...s, member })), []);

  return useMemo(
    () => ({ ...state, refetch, setMember }),
    [state, refetch, setMember],
  );
};
